import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import { REQUEST } from '@shared/common/injection-tokens/express.tokens';
import { AccountApi } from '@limehome/account-api-client';
import { cookieVariables } from '../../../constants/cookie-variables.constants';
import { CookieAppService } from '@shared/common/services/cookie-app/cookie-app.service';
import { languages } from '../../../constants/language.constants';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  constructor(
    private readonly translateService: TranslateService,
    public cookieAppService: CookieAppService,
    private readonly accountApi: AccountApi,
    @Inject(PLATFORM_ID) private readonly platformId: Object,
    @Optional() @Inject(REQUEST) private readonly request: Request
  ) {}

  changeLang(lang: string) {
    this.translateService.use(lang);
    this.cookieAppService.setCookie(cookieVariables.lang, lang);

    // Set the language for the account API
    this.accountApi.useLanguage(lang);

    if ((<any>window).zE && (<any>window).zE.setLocale) {
      (<any>window).zE.setLocale(lang);
    }
  }

  /**
   * Get the system language
   *
   * If the language is set in the cookie, use that. Otherwise use the language from the browser or
   * request. If all fails, default to english 😁.
   */
  getLang() {
    const lang = this.getLangFromCookie() || this.getLangFromBrowserOrRequest() || null;

    return this.isSupportedLanguage(lang) ? lang : languages.en;
  }

  /**
   * It returns the language from the cookie
   * @returns The cookie value of the lang cookie.
   */
  getLangFromCookie(): languages {
    return this.cookieAppService.getCookie(cookieVariables.lang) as languages;
  }

  /**
   * If we're on the browser, get the language from the browser, otherwise get it from the request.
   * @returns The language code of the browser or the request.
   */
  getLangFromBrowserOrRequest(): languages {
    if (this.isBrowser()) {
      /* Casting the return value of `getBrowserLang` to the type `languages`. */
      return this.translateService.getBrowserLang() as languages;
    }

    return (this.request.headers['accept-language'] || '').substring(0, 2) as languages;
  }

  /**
   * It determines whether we have support for a given language
   */
  isSupportedLanguage(lang: languages): boolean {
    return Object.values(languages).includes(lang);
  }

  /**
   * Returns true ff the platformId is a browser
   */
  isBrowser() {
    return isPlatformBrowser(this.platformId);
  }
}
